import { useEffect, useState } from 'react';
import { mealsApi } from '../api/endpoints';
import type { MediaAsset } from '../api/types';
import { FotoConEspera } from './FotoConEspera';

/** Lo que se espera antes de volver a pedir la URL firmada. */
const REINTENTO_MS = 900;

/**
 * RNF-07 · la imagen se sirve por una URL firmada de vigencia acotada, que se pide recién cuando
 * hay que mostrarla. El objeto nunca es público y la URL no se guarda: cuando vence, se pide otra.
 */
export function useSignedImageUrl(
  mealId: string,
  itemId: string,
  media: MediaAsset | null,
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const mediaId = media?.id ?? null;

  useEffect(() => {
    if (mediaId === null) {
      setUrl(null);
      return;
    }

    let active = true;
    let reintento: number | undefined;

    /*
     * Un reintento, y después sí se abandona.
     *
     * Firmar una URL es una llamada más contra el almacenamiento, y falla por cosas que se
     * arreglan solas: un corte de un segundo, la red del colectivo. Sin reintento, ese segundo
     * se llevaba puesta la foto de la comida hasta que la persona recargara la pantalla —y como
     * el fallo era mudo, parecía que la aplicación se había comido la foto.
     *
     * Uno solo, no cinco: si la segunda tampoco vuelve, el problema no es un parpadeo y
     * insistir sólo hace que la pantalla quede esperando algo que no va a llegar.
     */
    const pedir = (ultimoIntento: boolean) => {
      mealsApi
        .imageUrl(mealId, itemId, mediaId)
        .then((signed) => {
          if (active) {
            setUrl(signed.url);
          }
        })
        .catch(() => {
          if (!active) {
            return;
          }

          if (ultimoIntento) {
            setUrl(null);
            return;
          }

          reintento = window.setTimeout(() => pedir(true), REINTENTO_MS);
        });
    };

    pedir(false);

    return () => {
      active = false;
      window.clearTimeout(reintento);
    };
  }, [mealId, itemId, mediaId]);

  return url;
}

/**
 * La miniatura de una fila de ítem.
 *
 * Tres estados y ninguno finge ser otro: la foto real, `EAN` cuando el ítem vino de un código, y
 * el rayado cuando no hay imagen. Un cuadrado gris liso se leería como "la foto está cargando"
 * para siempre.
 */
export function ItemThumb({
  mealId,
  itemId,
  media,
  ean,
  alt,
}: {
  mealId: string;
  itemId: string;
  media: MediaAsset | null;
  ean: string | null;
  alt: string;
}) {
  const url = useSignedImageUrl(mealId, itemId, media);

  // Hay foto: se muestra su hueco latiendo hasta que la imagen termina de bajar. El rayado de
  // «no hay foto» es otra cosa y no debe aparecer mientras una está en camino.
  if (media !== null) {
    return <FotoConEspera url={url} alt={alt} className="item-thumb" />;
  }

  if (ean !== null && ean !== '') {
    return (
      <span className="item-thumb" aria-hidden="true">
        EAN
      </span>
    );
  }

  return <span className="item-thumb item-thumb-empty" aria-hidden="true" />;
}
